import * as React from 'react';
import {
  DefaultButton, Link, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ProgressIndicator, Spinner, SpinnerSize
} from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IBatchResult, IOperationLogEntry } from '../../../models/Operation';
import type { IBatchSummary, IUndoCheck, UndoEngine } from '../../../engine/UndoEngine';
import { describeGraphError } from '../../../utils/errors';
import { formatNumber } from '../../../utils/format';
import type { ITelemetry } from '../../../utils/telemetry';

export interface IUndoPanelProps {
  batch: IBatchSummary;
  engine: UndoEngine;
  auditLogUrl: string;
  telemetry: ITelemetry;
  libraryId: string;
  onDismiss: () => void;
  onRescan: () => void;
}

type Phase = 'checking' | 'review' | 'undoing' | 'done' | 'error';

function plural(n: number, one: string, many: string): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

/** Check → confirm → undo with progress → results. Mirrors the Apply panel. */
export default function UndoPanel(props: IUndoPanelProps): React.ReactElement {
  const { batch, engine, auditLogUrl, telemetry, libraryId, onDismiss, onRescan } = props;
  const [phase, setPhase] = React.useState<Phase>('checking');
  const [check, setCheck] = React.useState<IUndoCheck | undefined>(undefined);
  const [progress, setProgress] = React.useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [result, setResult] = React.useState<IBatchResult | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    let active: boolean = true;
    engine.check(batch)
      .then(c => {
        telemetry.event('undo.check', { libraryId, batchId: batch.batchId, ready: c.ready, blocked: c.problems.length });
        if (active) { setCheck(c); setPhase('review'); }
      })
      .catch(e => {
        telemetry.error('undo.checkFailed', e, { libraryId, batchId: batch.batchId });
        if (active) { setError(describeGraphError(e)); setPhase('error'); }
      });
    return () => { active = false; };
  }, [engine, batch, telemetry, libraryId]);

  const undo = (): void => {
    setPhase('undoing');
    engine.undo(batch, (done, total) => setProgress({ done, total }))
      .then(r => {
        const count = (status: string): number => r.entries.filter(e => e.status === status).length;
        telemetry.event('undo.finished', {
          libraryId, batchId: batch.batchId, undoBatchId: r.batchId,
          succeeded: count('success'), skipped: count('skipped'), failed: count('failed'), aborted: !!r.abortedReason
        });
        setResult(r);
        setPhase('done');
      })
      .catch(e => {
        telemetry.error('undo.failed', e, { libraryId, batchId: batch.batchId });
        setError(describeGraphError(e));
        setPhase('error');
      });
  };

  const failed: IOperationLogEntry[] = result ? result.entries.filter(e => e.status === 'failed') : [];
  const restored: number = result ? result.entries.filter(e => e.status === 'success').length : 0;

  const footer = (): React.ReactElement => {
    if (phase === 'review') {
      return (
        <div className={styles.changeActions}>
          <PrimaryButton text="Undo" iconProps={{ iconName: 'Undo' }} disabled={!check || check.ready === 0} onClick={undo} />
          <DefaultButton text="Cancel" onClick={onDismiss} />
        </div>
      );
    }
    if (phase === 'done' || phase === 'error') {
      return (
        <div className={styles.changeActions}>
          <PrimaryButton text="Rescan library" iconProps={{ iconName: 'Refresh' }} onClick={onRescan} />
          <DefaultButton text="Close" onClick={onDismiss} />
        </div>
      );
    }
    return <></>;
  };

  return (
    <Panel
      isOpen
      type={PanelType.medium}
      headerText="Undo batch"
      isBlocking={phase === 'undoing'}
      hasCloseButton={phase !== 'undoing'}
      onDismiss={phase === 'undoing' ? undefined : onDismiss}
      onRenderFooterContent={footer}
      isFooterAtBottom
    >
      {phase === 'checking' && <Spinner size={SpinnerSize.large} label="Checking the library…" />}

      {phase === 'review' && check && (
        <>
          <p>Every item was checked against the library as it is right now. Nothing has been changed yet.</p>
          <MessageBar messageBarType={check.ready > 0 ? MessageBarType.success : MessageBarType.warning} isMultiline>
            {plural(check.ready, 'item', 'items')} will go back to {check.ready === 1 ? 'its' : 'their'} original folder and name.
            {check.problems.length > 0 && ` ${plural(check.problems.length, 'item', 'items')} can't be undone and will be left where ${check.problems.length === 1 ? 'it is' : 'they are'}.`}
          </MessageBar>
          <p className={styles.issueListDescription}>
            Empty folders SharePoint Organizer created for quarantine are removed (to the site&apos;s recycle bin).
            Each step is recorded in the <Link href={auditLogUrl} target="_blank" rel="noreferrer">audit log</Link>.
          </p>
          {check.problems.length > 0 && (
            <>
              <h4 className={styles.applyHeading}>Will be left as they are</h4>
              <ul className={styles.applyList}>
                {check.problems.map(p => (
                  <li key={p.entry.operationId}>
                    <strong>{p.entry.originalName}</strong>
                    <div className={styles.applyProblem}>{p.message}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {phase === 'undoing' && (
        <ProgressIndicator
          label="Undoing…"
          description={`${formatNumber(progress.done)} of ${formatNumber(progress.total)} steps. Keep this page open.`}
          percentComplete={progress.total ? progress.done / progress.total : undefined}
        />
      )}

      {phase === 'done' && result && (
        <>
          {result.abortedReason && <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline>{result.abortedReason}</MessageBar>}
          <MessageBar messageBarType={failed.length || result.abortedReason ? MessageBarType.warning : MessageBarType.success} isMultiline>
            {plural(restored, 'step', 'steps')} completed{failed.length > 0 && `, ${formatNumber(failed.length)} could not be undone`}.
          </MessageBar>
          <p>Undo batch <code>{result.batchId}</code>. Rescan the library to see its current state.</p>
          {failed.length > 0 && (
            <ul className={styles.applyList}>
              {failed.map(e => (
                <li key={e.operationId}>
                  <strong>{e.sourcePath}</strong>
                  <div className={styles.applyProblem}>{e.errorMessage}</div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {phase === 'error' && <MessageBar messageBarType={MessageBarType.error} isMultiline>{error}</MessageBar>}
    </Panel>
  );
}
